import { Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useAppSelector } from "app/hooks";
import { MoreElements } from "components/MoreElements/MoreElements";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import React from "react";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import {
  CampaignCondition,
  CampaignConditionData,
  CampaignRuleTarget,
  ItemCriteriaParam,
} from "types/campaigns";
import { OLDate, formatTimeTo12Hours } from "utilities/dateFormat";
import { formatNumberValue } from "utilities/numbers";
import { getTransactionAttribute, isOldFormat } from "../../../campaignHelpers";
import {
  achievementConditions,
  eventConditions,
  transactionDirectCampaignConditions,
  transactionReferralCampaignConditions,
} from "../../rules";
import { ConditionError } from "./Condition";
import { AchievementsDropdown } from "./ResourcesReadOnly/AchievementsBox";
import { ChannelsBox } from "./ResourcesReadOnly/ChannelsBox";
import { CountriesBox } from "./ResourcesReadOnly/CountriesBox";
import { GroupsBox } from "./ResourcesReadOnly/GroupsBox";
import { SegmentBox } from "./ResourcesReadOnly/SegmentBox";
import { TiersBox } from "./ResourcesReadOnly/TiersBox";
import {
  checkIsAttributeSuppressedHj,
  conditionsWithParams,
  findConditionLabel,
  findConditionTypeLabel,
} from "./utils";

const parseErrorInfo = (error?: ConditionError) => {
  if (!error || !error.data) return "";
  if (Array.isArray(error.data)) return error.data.join();

  return "";
};

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    wrapper: {
      display: "flex",
      alignItems: "center",
      gap: "5px",
      flexWrap: "wrap",
      overflow: "hidden",
    },
    box: {
      border: `1px solid ${theme.gray200}`,
      padding: theme.spacing(3, 5),
      fontSize: "14px",
      borderRadius: theme.spacing(2),
      margin: theme.spacing(0, 2),
      textOverflow: "ellipsis",
      overflow: "hidden",
      alignSelf: "stretch",
    },
    boxWrapper: {
      overflow: "hidden",
      alignSelf: "stretch",
      display: "flex",
      alignItems: "center",
      "& > span": {
        display: "block",
      },
    },
    capitalize: {
      textTransform: "capitalize",
    },
    multiCondition: {
      display: "flex",
      alignItems: "center",
    },
    moreElements: {
      fontSize: "14px",
      color: theme.primary500,
    },
    operator: {
      textTransform: "lowercase",
    },
    textError: {
      color: theme.red500,
    },
    error: {
      border: `1px solid ${theme.red300}`,
      backgroundColor: theme.red50,
      color: theme.red800,
    },
  }),
);

export type ConditionType =
  | "achievementDropdown"
  | "array"
  | "boolean"
  | "date"
  | "datetime"
  | "expression"
  | "float"
  | "int"
  | "itemsCriteria"
  | "label"
  | "number"
  | "text"
  | "tierDropdown"
  | "tierDropdownSingle"
  | "channelDropdown"
  | "genderDropdown"
  | "countryDropdown"
  | "segmentDropdown"
  | "transactionType"
  | "object"
  | "collection"
  | "";

export type Condition = {
  attribute: string;
  data: CampaignConditionData;
  operator: string;
  type: ConditionType;
  param?: string | ItemCriteriaParam[];
};

interface Props {
  condition: Condition & { target?: CampaignRuleTarget };
  isRefferalCampaign: boolean;
  "data-test-id": string;
  disableTruncateData?: boolean;
  error?: ConditionError;
}

type DateType = {
  from: string;
  to: string;
};

type KeyPair = {
  key: string;
  value: string;
};

const renderType = (
  condition: Condition,
  message: any,
  classes: any,
  dataTestId: string,
  disableTruncateData?: boolean,
  error?: ConditionError,
) => {
  const errorInfo = parseErrorInfo(error);
  const shouldSuppressHj = checkIsAttributeSuppressedHj(condition?.attribute);
  if (
    condition.data === null ||
    condition.data === undefined ||
    condition.data === "" ||
    (Array.isArray(condition.data) && condition.data.length === 0) ||
    (condition.data as { groupOfValues: string[] })?.groupOfValues?.length === 0
  ) {
    return (
      <Tooltip text={errorInfo}>
        <div
          className={`${classes.box} ${error && error.data && classes.error}`}
        ></div>
      </Tooltip>
    );
  }

  const isMaxElementExceded =
    condition.data &&
    !disableTruncateData &&
    Array.isArray(condition.data) &&
    condition.data.length > 3;

  const maxVisibleConditionDataCount = 3;

  // exception condition, currently catched before switch as we do not categorize grouped values yet
  if (
    condition.operator === "is_one_of_group_values" ||
    condition.operator === "is_not_one_of_group_values"
  ) {
    const variableGroupsData = condition.data as { groupOfValues: string[] };

    return (
      <GroupsBox
        groupIds={variableGroupsData.groupOfValues}
        maxVisibleConditionDataCount={maxVisibleConditionDataCount}
      />
    );
  }

  switch (condition.type) {
    case "date":
    case "datetime":
      const isMultiDate = typeof condition.data === "object";

      if (isMultiDate) {
        const isExactMonthOrDay =
          condition.operator === "is_day_of_week" ||
          condition.operator === "is_month_of_year";

        if (isExactMonthOrDay) {
          if (isMaxElementExceded) {
            return (
              <div className={classes.wrapper}>
                {(condition.data as [string])
                  ?.slice(0, maxVisibleConditionDataCount)
                  ?.map((partDate, index) => {
                    return (
                      <div className={`${classes.box} ${classes.capitalize}`}>
                        {partDate}
                      </div>
                    );
                  })}
                <MoreElements
                  totalItemsNumber={(condition.data as [string])?.length}
                  tooltipData={{
                    type: "labels",
                    data: (condition.data as [string])?.slice(
                      maxVisibleConditionDataCount,
                    ),
                  }}
                />
              </div>
            );
          } else {
            return (
              <div className={classes.wrapper}>
                {(condition.data as [string]).map((partDate, index) => {
                  return (
                    <div className={`${classes.box} ${classes.capitalize}`}>
                      {partDate}
                    </div>
                  );
                })}
              </div>
            );
          }
        }

        const isFromDateError =
          error && error.data && (error.data as { from: string[] }).from;
        const isToDateError =
          error && error.data && (error.data as { to: string[] }).to;

        const getDateFrom = (data: DateType) => {
          if (condition.operator === "is_time_between") {
            return formatTimeTo12Hours(data.from);
          } else {
            return OLDate((condition.data as DateType).from);
          }
        };

        const getDateTo = (data: DateType) => {
          if (condition.operator === "is_time_between") {
            return formatTimeTo12Hours(data.to);
          } else {
            return OLDate((condition.data as DateType).to);
          }
        };

        return (
          <div className={classes.multiCondition}>
            <Tooltip
              text={
                isFromDateError
                  ? (error.data as { from: string[] }).from.join()
                  : ""
              }
            >
              <div
                className={`${classes.box} ${isFromDateError && classes.error}`}
              >
                {getDateFrom(condition.data as DateType)}
              </div>
            </Tooltip>
            {message({ id: "and" }).toLowerCase()}
            <Tooltip
              text={
                isToDateError ? (error.data as { to: string[] }).to.join() : ""
              }
            >
              <div
                className={`${classes.box} ${isToDateError && classes.error}`}
              >
                {getDateTo(condition.data as DateType)}
              </div>
            </Tooltip>
          </div>
        );
      }

      return (
        <span className={classes.box}>
          {condition.operator === "is_day_of_month"
            ? condition.data.toString()
            : OLDate(condition.data as string)}
        </span>
      );

    case "text":
    case "int":
    case "float":
    case "number":
    case "genderDropdown":
      const isMultiData = Array.isArray(condition.data);
      if (isMultiData) {
        if (isMaxElementExceded) {
          return (
            <div className={classes.multiCondition}>
              {(condition.data as [])
                ?.slice(0, maxVisibleConditionDataCount)
                ?.map((elem) => {
                  return <div className={`${classes.box}`}>{elem}</div>;
                })}
              <MoreElements
                totalItemsNumber={(condition.data as [])?.length}
                tooltipData={{
                  type: "labels",
                  data: (condition.data as [])?.slice(
                    maxVisibleConditionDataCount,
                  ),
                }}
              />
            </div>
          );
        }
        return (
          <div className={classes.multiCondition}>
            {(condition.data as []).map((elem) => {
              return <div className={`${classes.box}`}>{elem}</div>;
            })}
          </div>
        );
      }

      const isCurrencyValue = (attribute: string) =>
        [
          "customer.purchaseAmount",
          "customer.averagePurchaseAmount",
          "transaction.grossValue",
        ].includes(attribute);

      const formatData = (data: string) => {
        const minDecimalPlacesNumber = 2;

        if (["int", "float", "number"].includes(condition.type)) {
          return formatNumberValue(
            data,
            "",
            isCurrencyValue(condition.attribute)
              ? minDecimalPlacesNumber
              : undefined,
          );
        } else {
          return data;
        }
      };

      return (
        <div className={classes.box} data-hj-suppress={shouldSuppressHj}>
          {formatData(condition.data as string)}
        </div>
      );
    case "label":
      if (isMaxElementExceded) {
        return (
          <div className={classes.multiCondition}>
            {(condition.data as KeyPair[])
              ?.slice(0, maxVisibleConditionDataCount)
              ?.map((item) => {
                return (
                  <div className={classes.box}>
                    <span>{item.key}</span>:<span>{item.value}</span>
                  </div>
                );
              })}
            <MoreElements
              totalItemsNumber={(condition.data as KeyPair[])?.length}
              tooltipData={{
                type: "keyValue",
                data: (condition.data as KeyPair[])?.slice(
                  maxVisibleConditionDataCount,
                ),
              }}
            />
          </div>
        );
      }
      return (
        <div className={classes.multiCondition}>
          {(condition.data as KeyPair[]).map((item) => {
            return (
              <div className={classes.box}>
                <span>{item.key}</span>:<span>{item.value}</span>
              </div>
            );
          })}
        </div>
      );
    case "boolean":
    case "expression":
      return <div className={classes.box}>{`${condition.data}`}</div>;
    case "segmentDropdown":
      return (
        <SegmentBox
          id={condition.data as string}
          data-test-id={`${dataTestId}-segment`}
        />
      );
    case "tierDropdown":
      return (
        <div className={classes.wrapper}>
          <TiersBox
            ids={condition.data as [string]}
            data-test-id={`${dataTestId}-tiers`}
            maxVisibleConditionDataCount={maxVisibleConditionDataCount}
          />
        </div>
      );
    case "tierDropdownSingle":
      return (
        <div className={classes.wrapper}>
          <TiersBox
            ids={condition.data as string}
            data-test-id={`${dataTestId}-tiers`}
            maxVisibleConditionDataCount={maxVisibleConditionDataCount}
            multiple={false}
          />
        </div>
      );
    case "channelDropdown":
      return (
        <ChannelsBox
          ids={condition.data as [string]}
          maxVisibleConditionDataCount={maxVisibleConditionDataCount}
          data-test-id={`${dataTestId}-channel`}
        />
      );
    case "countryDropdown":
      return (
        <CountriesBox
          isoCodes={condition.data as [string]}
          maxVisibleConditionDataCount={maxVisibleConditionDataCount}
        />
      );
    case "achievementDropdown":
      return (
        <AchievementsDropdown
          ids={condition.data as [string]}
          maxVisibleConditionDataCount={maxVisibleConditionDataCount}
        />
      );
    case "transactionType":
      return (
        <div className={classes.box}>
          {message({ id: condition.data as string })}
        </div>
      );
    case "itemsCriteria":
      return (
        <div className={classes.wrapper}>
          <div className={classes.box}>{condition.data as string}</div>
          <Typography className={error ? classes.textError : ""} fontSize={14}>
            {message({ id: "where" }).toLowerCase()}
          </Typography>
          {(condition.param as ItemCriteriaParam[])?.map((item, index) => {
            const areValuesInParam = item.values.length > 0;
            const isAndOperatorVisible =
              index !== 0 &&
              index < condition?.param?.length! &&
              areValuesInParam;

            return (
              <React.Fragment key={index}>
                {isAndOperatorVisible ? (
                  <Typography>
                    {message({ id: "and" }).toLowerCase()}
                  </Typography>
                ) : null}
                {areValuesInParam ? (
                  <div className={classes.box}>
                    {message({ id: prepareCriterionLabel(item.criterion) })}
                  </div>
                ) : null}
                {item.criterion === "productCustomAttribute" &&
                areValuesInParam ? (
                  <Typography>
                    {message({
                      id: "hasAtLeastOneCustomAttribute",
                    }).toLowerCase()}
                  </Typography>
                ) : null}
                {item.values.map((value, index) => (
                  <React.Fragment key={index}>
                    {typeof value === "string" ? (
                      <div className={classes.box}>{value}</div>
                    ) : (
                      <div className={classes.box}>
                        <span>{value.key}</span>:<span>{value.value}</span>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </React.Fragment>
            );
          })}
        </div>
      );
    default:
      return null;
  }
};

const prepareCriterionLabel = (criterion: string) => {
  switch (criterion) {
    case "productBrand":
      return "brand";
    case "productCategory":
      return "category";
    case "productCustomAttribute":
      return "customAttribute";
    case "productName":
      return "name";
  }
};

export const ConditionReadOnly = ({
  condition,
  isRefferalCampaign,
  "data-test-id": dataTestId,
  disableTruncateData,
  error,
}: Props) => {
  const { message } = useMessage();
  const classes = useStyles();
  const defaultWallet = useAppSelector(selectDefaultWalletData);
  const { currency } = useAppSelector(selectCurrentStoreData);
  const attribute = getTransactionAttribute(condition.attribute);
  const customAttributeKey = (condition.data as { attributeKey?: string })
    ?.attributeKey;
  const isOperatorError = error && error.operator;
  const isParamError = error && error.param;

  const isConditionParamVisible = conditionsWithParams.includes(
    condition.attribute,
  );
  const oldFormatTransactionReferralCampaignConditions =
    transactionReferralCampaignConditions.filter((item) =>
      isOldFormat(item),
    ) as CampaignCondition[];
  const oldFormatTransactionDirectCampaignConditions =
    transactionDirectCampaignConditions.filter((item) =>
      isOldFormat(item),
    ) as CampaignCondition[];

  return (
    <div className={classes.wrapper}>
      <div className={classes.box} data-test-id={`${dataTestId}-attribute`}>
        {message(
          {
            id: findConditionTypeLabel({ ...condition, attribute: attribute }, [
              ...achievementConditions,
              ...eventConditions,
              ...[
                ...(isRefferalCampaign
                  ? oldFormatTransactionReferralCampaignConditions
                  : oldFormatTransactionDirectCampaignConditions),
              ],
            ]),
          },
          {
            walletName: defaultWallet?.name,
            units: defaultWallet?.unitPluralName,
            currency,
          },
        )}
      </div>

      {customAttributeKey ? (
        <>
          <Typography>{message({ id: "withAKey" }).toLowerCase()}</Typography>
          <div className={classes.box}>{customAttributeKey}</div>
        </>
      ) : null}

      {isConditionParamVisible && (
        <div className={`${classes.box} ${isParamError ? classes.error : ""}`}>
          {typeof condition.param === "string" ? condition.param : ""}
        </div>
      )}

      <Tooltip
        text={isOperatorError ? (error.operator as string[]).join() : ""}
      >
        <div
          className={`${classes.operator} ${
            isOperatorError && classes.textError
          }`}
          data-test-id={`${dataTestId}-operator`}
        >
          {condition.operator
            ? message({
                id: findConditionLabel(condition.operator),
              }).toLowerCase()
            : message({ id: "condition" })}
        </div>
      </Tooltip>

      <div data-test-id={`${dataTestId}-data`} className={classes.boxWrapper}>
        {renderType(
          condition,
          message,
          classes,
          dataTestId,
          disableTruncateData,
          error,
        )}
      </div>
    </div>
  );
};
